import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BrainCircuit, CalendarDays, Check, CheckCircle2, ChevronLeft, ChevronRight, Loader2, Power, PowerOff, RefreshCw, Search, ShieldCheck, UserPlus, UsersRound } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { hasFullAppAccess } from '@/lib/accessControl';
import {
  AdminUserRoleRecord,
  getCurrentUser,
  listAdminUsers,
  updateAdminUserAIMealPlanCohort,
  updateAdminUserRoles,
  updateAdminUserStatus,
  upsertAdminAIMealPlanCohorts,
  upsertPreProvisionedUserRoles,
} from '@/lib/api/auth';
import { getClientsDashboard } from '@/lib/api/clients';
import type { ClientDashboardItem } from '@/types/client';
import {
  AI_MEAL_PLAN_COHORT_OPTIONS,
  DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD,
  getAIMealPlanCohortLabel,
  type AIMealPlanCohort,
  type AIMealPlanCohortPayload,
} from '@/constants/ai-model-options';

const ADMIN_ROLE = 'admin';
const SUPER_ADMIN_ROLE = 'super_admin';
const CLIENT_ROLE = 'client';
const PRE_PROVISION_ROLE = 'recipe_ingredient_manager';
const ASSIGNABLE_ROLES = ['nutritionist', CLIENT_ROLE, ADMIN_ROLE, SUPER_ADMIN_ROLE, PRE_PROVISION_ROLE] as const;
const ADMIN_LEVEL_ROLES = new Set<string>([ADMIN_ROLE, SUPER_ADMIN_ROLE]);
const PRIMARY_SUPER_ADMIN_EMAIL = 'mrinal@caloriescience.ai';
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_AI_MEAL_PLAN_COHORT: AIMealPlanCohort = 'hybrid';

type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

const ROLE_LABELS: Record<AssignableRole, string> = {
  nutritionist: 'Nutritionist',
  client: 'Client',
  admin: 'Admin',
  super_admin: 'Super admin',
  recipe_ingredient_manager: 'Recipe and ingredient manager',
};

const getUserAIMealPlanCohort = (user: AdminUserRoleRecord): AIMealPlanCohortPayload =>
  user.aiMealPlanCohort ?? DEFAULT_AI_MEAL_PLAN_COHORT_PAYLOAD;

const getAIMealPlanCohortSourceLabel = (cohort: AIMealPlanCohortPayload): string => {
  if (cohort.assignedCohort) {
    return 'Assigned';
  }

  return cohort.effectiveCohort === 'internal' ? 'Internal team' : 'New user';
};

const parseEmails = (value: string): string[] => {
  const normalized = value
    .split(/[\s,;]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set(normalized));
};

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatDate = (value: string | null): string => {
  if (!value) {
    return 'Never';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
};

const normalizeAssignableRoles = (roles: string[]): string[] => {
  const normalized = ASSIGNABLE_ROLES.filter((role) => roles.includes(role));
  return normalized.length ? normalized : ['nutritionist'];
};

const hasAdminLevelRole = (roles: string[]): boolean =>
  roles.some((role) => ADMIN_LEVEL_ROLES.has(role));

interface RoleMultiSelectProps {
  roles: string[];
  disabled: boolean;
  canManageAdminRoles: boolean;
  onSave: (roles: string[]) => void;
}

const RoleMultiSelect: React.FC<RoleMultiSelectProps> = ({ roles, disabled, canManageAdminRoles, onSave }) => {
  const [open, setOpen] = useState(false);
  const [draftRoles, setDraftRoles] = useState<string[]>(() => normalizeAssignableRoles(roles));

  useEffect(() => {
    if (!open) {
      setDraftRoles(normalizeAssignableRoles(roles));
    }
  }, [open, roles]);

  const toggleRole = (role: AssignableRole) => {
    if (ADMIN_LEVEL_ROLES.has(role) && !canManageAdminRoles) {
      return;
    }

    setDraftRoles((current) => {
      if (current.includes(role)) {
        const nextRoles = current.filter((currentRole) => currentRole !== role);
        return nextRoles.length ? nextRoles : current;
      }
      return normalizeAssignableRoles([...current, role]);
    });
  };

  const hasChanges =
    normalizeAssignableRoles(roles).join('|') !== normalizeAssignableRoles(draftRoles).join('|');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="h-9 w-full justify-start overflow-hidden border-[#cde3e9] px-3 text-left font-normal"
        >
          <span className="flex min-w-0 items-center gap-1">
            {normalizeAssignableRoles(roles).slice(0, 2).map((role) => (
              <Badge key={role} variant="outline" className="shrink-0 border-[#cde3e9] bg-[#f7fbfc] text-[#334650]">
                {ROLE_LABELS[role as AssignableRole] ?? role}
              </Badge>
            ))}
            {normalizeAssignableRoles(roles).length > 2 ? (
              <span className="shrink-0 rounded-md bg-[#eef4f6] px-1.5 py-0.5 text-xs font-medium text-[#5f7580]">
                +{normalizeAssignableRoles(roles).length - 2}
              </span>
            ) : null}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <div className="space-y-2">
          {ASSIGNABLE_ROLES.map((role) => {
            const isAdminLevelOption = ADMIN_LEVEL_ROLES.has(role);
            const isDisabledOption = isAdminLevelOption && !canManageAdminRoles;
            return (
              <label
                key={role}
                className={`flex items-center gap-3 rounded-md px-2 py-2 text-sm ${
                  isDisabledOption
                    ? 'cursor-not-allowed text-[#9aa9b0]'
                    : 'cursor-pointer text-[#334650] hover:bg-[#f5fafc]'
                }`}
              >
                <Checkbox
                  checked={draftRoles.includes(role)}
                  disabled={isDisabledOption}
                  onCheckedChange={() => toggleRole(role)}
                />
                <span>{ROLE_LABELS[role]}</span>
              </label>
            );
          })}
        </div>
        {!canManageAdminRoles ? (
          <p className="mt-3 rounded-md bg-[#f5fafc] px-3 py-2 text-xs leading-5 text-[#5d6f78]">
            Admin and Super admin changes are limited to the primary super admin.
          </p>
        ) : null}
        <div className="mt-3 flex items-center justify-end gap-2 border-t border-[#e4eef2] pt-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!hasChanges || draftRoles.length === 0}
            onClick={() => {
              onSave(normalizeAssignableRoles(draftRoles));
              setOpen(false);
            }}
          >
            Save roles
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number | string;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
}> = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#17262f]">{label}</p>
        <p className="mt-3 text-4xl font-bold leading-none tracking-tight text-[#17262f]">{value}</p>
      </div>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#f1f6f8] text-[#5f7580]">
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-3 min-h-5 text-sm text-[#8497a0]">{detail}</p>
  </div>
);

const UserProvisioningPage: React.FC = () => {
  const [rawEmails, setRawEmails] = useState('');
  const [assignRolesEnabled, setAssignRolesEnabled] = useState(true);
  const [provisionRoles, setProvisionRoles] = useState<string[]>([PRE_PROVISION_ROLE]);
  const [assignCohortEnabled, setAssignCohortEnabled] = useState(false);
  const [cohortToAssign, setCohortToAssign] = useState<AIMealPlanCohort>(DEFAULT_AI_MEAL_PLAN_COHORT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<AdminUserRoleRecord[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [invitedClients, setInvitedClients] = useState<ClientDashboardItem[]>([]);
  const [isLoadingInvitedClients, setIsLoadingInvitedClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [invitedClientTotal, setInvitedClientTotal] = useState(0);
  const [lastResult, setLastResult] = useState<{
    rolesCount: number | null;
    cohortsCount: number | null;
    existingUsersUpdated: number;
  } | null>(null);

  const currentUser = useMemo(() => getCurrentUser(), []);
  const currentUserRoles = Array.isArray(currentUser?.roles)
    ? currentUser.roles.map((role) => String(role || '').trim()).filter(Boolean)
    : [];
  const canManageAdminRoles =
    currentUser?.email?.toLowerCase() === PRIMARY_SUPER_ADMIN_EMAIL
    || currentUserRoles.includes(SUPER_ADMIN_ROLE);
  const canViewInvitedClients = hasFullAppAccess(currentUser);

  const emails = useMemo(() => parseEmails(rawEmails), [rawEmails]);
  const invalidEmails = useMemo(() => emails.filter((email) => !isValidEmail(email)), [emails]);
  const hasAssignmentSelected = assignRolesEnabled || assignCohortEnabled;
  const rolesSelectionValid = !assignRolesEnabled || provisionRoles.length > 0;
  const canSubmit =
    emails.length > 0
    && invalidEmails.length === 0
    && hasAssignmentSelected
    && rolesSelectionValid
    && !isSubmitting;

  const toggleProvisionRole = (role: AssignableRole) => {
    if (ADMIN_LEVEL_ROLES.has(role) && !canManageAdminRoles) {
      return;
    }
    setProvisionRoles((current) =>
      current.includes(role) ? current.filter((value) => value !== role) : [...current, role],
    );
  };

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await listAdminUsers(search);
      setUsers(response.data.items);
      setPage(1);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load users right now.';

      toast({
        title: 'Users failed to load',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [search]);

  const loadInvitedClients = useCallback(async () => {
    setIsLoadingInvitedClients(true);
    try {
      const response = await getClientsDashboard({
        status: 'all',
        search: clientSearch.trim() || undefined,
        page: 1,
        limit: 10,
      });
      setInvitedClients(response.clients);
      setInvitedClientTotal(response.pagination.total);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load invited clients.';

      toast({
        title: 'Client list failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingInvitedClients(false);
    }
  }, [clientSearch]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!canViewInvitedClients) {
      setInvitedClients([]);
      setInvitedClientTotal(0);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadInvitedClients();
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [canViewInvitedClients, loadInvitedClients]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setLastResult(null);

    try {
      const [rolesResponse, cohortResponse] = await Promise.all([
        assignRolesEnabled
          ? upsertPreProvisionedUserRoles(emails.map((email) => ({ email, roles: provisionRoles })))
          : Promise.resolve(null),
        assignCohortEnabled
          ? upsertAdminAIMealPlanCohorts(emails.map((email) => ({ email, cohort: cohortToAssign })))
          : Promise.resolve(null),
      ]);

      const existingUsersUpdated = Math.max(
        rolesResponse?.data.items.filter((item) => item.existingUserUpdated).length ?? 0,
        cohortResponse?.data.items.filter((item) => item.existingUserUpdated).length ?? 0,
      );

      setLastResult({
        rolesCount: rolesResponse?.data.count ?? null,
        cohortsCount: cohortResponse?.data.count ?? null,
        existingUsersUpdated,
      });

      if (rolesResponse || cohortResponse) {
        setUsers((currentUsers) =>
          currentUsers.map((user) => {
            const roleUpdate = rolesResponse?.data.items.find((item) => item.email === user.email.toLowerCase());
            const cohortUpdate = cohortResponse?.data.items.find((item) => item.email === user.email.toLowerCase());
            if (!roleUpdate && !cohortUpdate) {
              return user;
            }
            return {
              ...user,
              ...(roleUpdate ? { roles: roleUpdate.roles } : {}),
              ...(cohortUpdate ? { aiMealPlanCohort: cohortUpdate.aiMealPlanCohort } : {}),
            };
          }),
        );
      }

      setRawEmails('');

      const parts: string[] = [];
      if (rolesResponse) {
        parts.push(`${rolesResponse.data.count} role assignment${rolesResponse.data.count === 1 ? '' : 's'}`);
      }
      if (cohortResponse) {
        parts.push(`${cohortResponse.data.count} cohort (${getAIMealPlanCohortLabel(cohortToAssign)}) assignment${cohortResponse.data.count === 1 ? '' : 's'}`);
      }
      toast({
        title: 'Pre-provisioning saved',
        description: `Saved ${parts.join(' and ')}.`,
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to pre-provision users right now.';

      toast({
        title: 'Provisioning failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = users.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const pageEndIndex = Math.min(users.length, safePage * pageSize);
  const paginatedUsers = useMemo(
    () => users.slice((safePage - 1) * pageSize, safePage * pageSize),
    [pageSize, safePage, users],
  );
  const enabledCount = useMemo(() => users.filter((user) => !user.disabled).length, [users]);
  const disabledCount = useMemo(() => users.filter((user) => user.disabled).length, [users]);
  const adminCount = useMemo(() => users.filter((user) => hasAdminLevelRole(user.roles)).length, [users]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleRoleChange = async (user: AdminUserRoleRecord, nextRoles: string[]) => {
    setUpdatingUserId(user.id);

    try {
      const response = await updateAdminUserRoles(user.id, nextRoles);
      setUsers((currentUsers) =>
        currentUsers.map((currentUser) =>
          currentUser.id === user.id
            ? {
                ...currentUser,
                roles: response.data.roles,
                updatedAt: response.data.updatedAt,
              }
            : currentUser,
        ),
      );
      toast({
        title: 'Roles updated',
        description: `${user.email} now has ${response.data.roles.length} role${response.data.roles.length === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to update this user role.';

      toast({
        title: 'Role update failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAIMealPlanCohortChange = async (user: AdminUserRoleRecord, cohort: AIMealPlanCohort) => {
    setUpdatingUserId(user.id);

    try {
      const response = await updateAdminUserAIMealPlanCohort(user.id, cohort);
      setUsers((currentUsers) =>
        currentUsers.map((currentUser) =>
          currentUser.id === user.id
            ? {
                ...currentUser,
                aiMealPlanCohort: response.data.aiMealPlanCohort,
                updatedAt: response.data.updatedAt,
              }
            : currentUser,
        ),
      );
      toast({
        title: 'AI cohort updated',
        description: `${user.email} now uses ${getAIMealPlanCohortLabel(response.data.aiMealPlanCohort.effectiveCohort)}.`,
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to update this AI meal-plan cohort.';

      toast({
        title: 'AI cohort update failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleStatusChange = async (user: AdminUserRoleRecord, disabled: boolean) => {
    setUpdatingUserId(user.id);

    try {
      const response = await updateAdminUserStatus(user.id, disabled);
      setUsers((currentUsers) =>
        currentUsers.map((currentUser) =>
          currentUser.id === user.id
            ? {
                ...currentUser,
                disabled: response.data.disabled,
                disabledAt: response.data.disabledAt,
                disabledBy: response.data.disabledBy,
                disabledReason: response.data.disabledReason,
                updatedAt: response.data.updatedAt,
              }
            : currentUser,
        ),
      );
      toast({
        title: disabled ? 'User disabled' : 'User enabled',
        description: `${user.email} is now ${disabled ? 'disabled' : 'enabled'}.`,
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to update this user status.';

      toast({
        title: 'Status update failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <main className="min-h-[calc(100vh-7.5rem)] overflow-hidden rounded-[28px] border border-[#dbe6eb] bg-white px-4 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3.5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#167F9D] text-white shadow-sm">
              <UsersRound className="h-6 w-6" />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#17262f]">Admin · Access control</p>
              <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-[#17262f]">Role Management</h1>
              <p className="mt-0.5 text-sm text-[#5d6f78]">Manage user roles, AI cohorts, and account access.</p>
            </div>
          </div>
          <div className="flex w-fit items-center gap-2 rounded-full border border-[#dbe8ed] bg-white px-4 py-2 text-sm font-medium text-[#5f7580] shadow-sm">
            <ShieldCheck className="h-4 w-4 text-[#167F9D]" />
            Admin-only access
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Total users"
            value={users.length}
            detail="Matching the current search"
            icon={UsersRound}
          />
          <StatCard
            label="Enabled"
            value={enabledCount}
            detail="Active accounts on this list"
            icon={CheckCircle2}
          />
          <StatCard
            label="Disabled"
            value={disabledCount}
            detail="Suspended accounts on this list"
            icon={PowerOff}
          />
          <StatCard
            label="Admins"
            value={adminCount}
            detail="Admin / super admin roles"
            icon={ShieldCheck}
          />
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm sm:p-6"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#eaf4f8] text-[#167F9D]">
              <UserPlus className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-[#17262f]">Pre-provision users</h2>
              <p className="mt-1 text-sm leading-6 text-[#5d6f78]">
                Grant access before people sign up. Enter emails, choose what to assign, and it applies on their first login. Existing users are updated immediately.
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            {/* Step 1 — who */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[#17262f]" htmlFor="provision-emails">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#eaf4f8] text-[11px] font-bold text-[#167F9D]">1</span>
                Email addresses
              </label>
              <Textarea
                id="provision-emails"
                value={rawEmails}
                onChange={(event) => setRawEmails(event.target.value)}
                rows={7}
                placeholder="alex@example.com, sam@example.com"
                disabled={isSubmitting}
              />
              {invalidEmails.length > 0 ? (
                <p className="text-sm font-medium text-destructive">
                  Fix invalid email{invalidEmails.length === 1 ? '' : 's'}: {invalidEmails.join(', ')}
                </p>
              ) : (
                <p className="text-sm text-[#6b7c85]">
                  {emails.length} unique email{emails.length === 1 ? '' : 's'} ready. Separate with commas, spaces, or new lines.
                </p>
              )}
            </div>

            {/* Step 2 — what to assign */}
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#17262f]">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#eaf4f8] text-[11px] font-bold text-[#167F9D]">2</span>
                What to assign
              </p>

              {/* Roles */}
              <div className={`rounded-xl border p-4 transition-colors ${assignRolesEnabled ? 'border-[#bfe0ea] bg-[#f7fcfd]' : 'border-[#e4edf1] bg-white'}`}>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <Checkbox
                    checked={assignRolesEnabled}
                    onCheckedChange={(checked) => setAssignRolesEnabled(checked === true)}
                    disabled={isSubmitting}
                  />
                  <span className="flex items-center gap-2 text-sm font-medium text-[#17262f]">
                    <ShieldCheck className="h-4 w-4 text-[#167F9D]" />
                    Assign roles
                  </span>
                </label>
                <div className={`mt-3 flex flex-wrap gap-2 ${assignRolesEnabled ? '' : 'pointer-events-none opacity-45'}`}>
                  {ASSIGNABLE_ROLES.map((role) => {
                    const selected = provisionRoles.includes(role);
                    const roleDisabled = !assignRolesEnabled || isSubmitting || (ADMIN_LEVEL_ROLES.has(role) && !canManageAdminRoles);
                    return (
                      <button
                        key={role}
                        type="button"
                        disabled={roleDisabled}
                        onClick={() => toggleProvisionRole(role)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          selected
                            ? 'border-[#167F9D] bg-[#167F9D] text-white'
                            : 'border-[#cde3e9] bg-white text-[#334650] hover:border-[#9fcdda]'
                        } ${roleDisabled && !selected ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {selected ? <Check className="h-3.5 w-3.5" /> : null}
                        {ROLE_LABELS[role]}
                      </button>
                    );
                  })}
                </div>
                {assignRolesEnabled && provisionRoles.length === 0 ? (
                  <p className="mt-2 text-xs font-medium text-destructive">Select at least one role.</p>
                ) : !canManageAdminRoles ? (
                  <p className="mt-2 text-xs text-[#8497a0]">Admin and Super admin roles are limited to the primary super admin.</p>
                ) : null}
              </div>

              {/* Cohort */}
              <div className={`rounded-xl border p-4 transition-colors ${assignCohortEnabled ? 'border-[#bfe0ea] bg-[#f7fcfd]' : 'border-[#e4edf1] bg-white'}`}>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <Checkbox
                    checked={assignCohortEnabled}
                    onCheckedChange={(checked) => setAssignCohortEnabled(checked === true)}
                    disabled={isSubmitting}
                  />
                  <span className="flex items-center gap-2 text-sm font-medium text-[#17262f]">
                    <BrainCircuit className="h-4 w-4 text-[#167F9D]" />
                    Assign AI meal-plan cohort
                  </span>
                </label>
                <div className={`mt-3 ${assignCohortEnabled ? '' : 'pointer-events-none opacity-45'}`}>
                  <Select
                    value={cohortToAssign}
                    onValueChange={(value) => setCohortToAssign(value as AIMealPlanCohort)}
                    disabled={!assignCohortEnabled || isSubmitting}
                  >
                    <SelectTrigger id="ai-cohort-select" className="border-[#cde3e9] bg-white sm:max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_MEAL_PLAN_COHORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-2 text-xs text-[#8497a0]">Applies to nutritionist meal-plan generation.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-[#eef4f6] pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-h-5 text-sm text-[#6b7c85]">
              {lastResult ? (
                <span className="inline-flex items-center gap-2 text-[#334650]">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[#21a67a]" />
                  Last save:
                  {lastResult.rolesCount !== null ? ` ${lastResult.rolesCount} role${lastResult.rolesCount === 1 ? '' : 's'}` : ''}
                  {lastResult.rolesCount !== null && lastResult.cohortsCount !== null ? ' ·' : ''}
                  {lastResult.cohortsCount !== null ? ` ${lastResult.cohortsCount} cohort${lastResult.cohortsCount === 1 ? '' : 's'}` : ''}
                  {lastResult.existingUsersUpdated > 0 ? ` · ${lastResult.existingUsersUpdated} existing updated` : ''}
                </span>
              ) : (
                'No provisioning changes in this session.'
              )}
            </div>
            <Button type="submit" disabled={!canSubmit} className="sm:min-w-48">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Save pre-provisioning
            </Button>
          </div>
        </form>

        {canViewInvitedClients ? (
          <section className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-3">
                <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf4f8] text-[#167F9D] sm:flex">
                  <UserPlus className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#17262f]">Directory</p>
                  <h2 className="mt-1 text-lg font-semibold text-[#17262f]">Invited clients</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-[#5d6f78]">
                    Client profiles appear here as soon as an invite is sent. They appear in Role Management only after the client completes OTP login.
                  </p>
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
                <div className="relative sm:w-72">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7c85]" />
                  <Input
                    value={clientSearch}
                    onChange={(event) => setClientSearch(event.target.value)}
                    placeholder="Search client email"
                    className="pl-9"
                  />
                </div>
                <Button type="button" variant="outline" onClick={() => void loadInvitedClients()} disabled={isLoadingInvitedClients}>
                  {isLoadingInvitedClients ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Refresh
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 text-sm text-[#5d6f78] sm:flex-row sm:items-center sm:justify-between">
              <span>
                Showing {invitedClients.length} of {invitedClientTotal} invited client{invitedClientTotal === 1 ? '' : 's'}
              </span>
              <Button asChild variant="outline" size="sm">
                <Link to="/clients?status=active">Open My Clients</Link>
              </Button>
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-[#dbe8ed]">
              <div className="grid grid-cols-[minmax(12rem,1.2fr)_minmax(13rem,1.2fr)_minmax(8rem,0.7fr)_minmax(8rem,0.6fr)] border-b border-[#dbe8ed] bg-[#eff7fa] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#17262f] max-lg:hidden">
                <span>Client</span>
                <span>Email</span>
                <span>Location</span>
                <span>Status</span>
              </div>

              {isLoadingInvitedClients && invitedClients.length === 0 ? (
                <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-[#5d6f78]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading invited clients
                </div>
              ) : invitedClients.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-sm text-[#5d6f78]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#eef5f8] text-[#94a8b0]">
                    <UsersRound className="h-5 w-5" />
                  </span>
                  No invited clients found.
                </div>
              ) : (
                invitedClients.map((client) => (
                  <div
                    key={client.id}
                    className="grid gap-4 border-t border-[#e4eef2] px-4 py-4 transition-colors first:border-t-0 hover:bg-[#f9fcfd] lg:grid-cols-[minmax(12rem,1.2fr)_minmax(13rem,1.2fr)_minmax(8rem,0.7fr)_minmax(8rem,0.6fr)] lg:items-center"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/clients/${client.id}/details-v2/overview`}
                        className="block truncate text-sm font-semibold text-[#17262f] underline-offset-2 hover:underline"
                      >
                        {client.fullName || client.id}
                      </Link>
                      <p className="mt-1 truncate text-xs text-[#6b7c85]">{client.id}</p>
                    </div>
                    <p className="truncate text-sm text-[#334650]">{client.email || '-'}</p>
                    <p className="truncate text-sm text-[#5d6f78]">{client.location || '-'}</p>
                    <div>
                      <Badge variant="outline" className="border-[#cde3e9] bg-[#f7fbfc] capitalize text-[#334650]">
                        {client.status || 'active'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        <section className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf4f8] text-[#167F9D] sm:flex">
                <UsersRound className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#8497a0]">Access control</p>
                <h2 className="mt-1 text-lg font-semibold text-[#17262f]">Existing users</h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-[#5d6f78]">
                  Review users page by page, assign access roles, and manage account status.
                </p>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
              <div className="relative sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7c85]" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search email"
                  className="pl-9"
                />
              </div>
              <Button type="button" variant="outline" onClick={() => void loadUsers()} disabled={isLoadingUsers}>
                {isLoadingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </Button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 text-sm text-[#5d6f78] sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing {pageStartIndex}-{pageEndIndex} of {users.length} user{users.length === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-9 w-24 border-[#cde3e9]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-[#dbe8ed]">
           <div className="overflow-x-auto">
            <div className="min-w-[1120px]">
            <div className="grid grid-cols-[minmax(13rem,1.3fr)_10rem_minmax(11rem,0.85fr)_minmax(13rem,1fr)_13rem_9rem] border-b border-[#dbe8ed] bg-[#eff7fa] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#17262f]">
              <span>User</span>
              <span>Status</span>
              <span>Current roles</span>
              <span>Assign roles</span>
              <span>AI cohort</span>
              <span>Last login</span>
            </div>

            {isLoadingUsers && users.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-[#5d6f78]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading users
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-sm text-[#5d6f78]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#eef5f8] text-[#94a8b0]">
                  <UsersRound className="h-5 w-5" />
                </span>
                No users found.
              </div>
            ) : (
              paginatedUsers.map((user) => {
                const isUpdating = updatingUserId === user.id;
                const isCurrentUser = currentUser?.email?.toLowerCase() === user.email.toLowerCase();
                const statusActionDisabled =
                  isUpdating || isCurrentUser || (!canManageAdminRoles && hasAdminLevelRole(user.roles));
                const aiCohort = getUserAIMealPlanCohort(user);
                const aiCohortDisabled =
                  isUpdating
                  || (
                    user.userType !== 'nutritionist'
                    && !user.roles.includes('nutritionist')
                  );
                return (
                  <div
                    key={user.id}
                    className="grid grid-cols-[minmax(13rem,1.3fr)_10rem_minmax(11rem,0.85fr)_minmax(13rem,1fr)_13rem_9rem] items-center gap-4 border-t border-[#eef4f6] px-4 py-3 transition-colors first:border-t-0 odd:bg-white even:bg-[#fafcfd] hover:bg-[#f2f9fb]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eef4f7] text-xs font-semibold uppercase text-[#5f7580]">
                        {(user.displayName || user.email || '?').trim().charAt(0)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#17262f]">{user.displayName || user.email}</p>
                        <p className="mt-0.5 truncate text-xs text-[#6b7c85]">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          user.disabled
                            ? 'gap-1.5 border-[#f0c9c9] bg-[#fff6f6] text-[#a33b3b]'
                            : 'gap-1.5 border-[#bfe4d2] bg-[#f3fbf7] text-[#1f7a55]'
                        }
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${user.disabled ? 'bg-[#d3564f]' : 'bg-[#26a06f]'}`} />
                        {user.disabled ? 'Disabled' : 'Enabled'}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={statusActionDisabled}
                        onClick={() => void handleStatusChange(user, !user.disabled)}
                        aria-label={user.disabled ? 'Enable user' : 'Disable user'}
                        title={user.disabled ? 'Enable user' : 'Disable user'}
                        className={
                          user.disabled
                            ? 'h-8 w-8 shrink-0 p-0 text-[#1f7a55] hover:bg-[#f3fbf7] hover:text-[#166845]'
                            : 'h-8 w-8 shrink-0 p-0 text-[#a33b3b] hover:bg-[#fff6f6] hover:text-[#8a2f2f]'
                        }
                      >
                        {user.disabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                      </Button>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5">
                      {user.roles.slice(0, 2).map((role) => (
                        <Badge key={role} variant="outline" className="shrink-0 border-[#cde3e9] bg-[#f7fbfc] text-[#334650]">
                          {ROLE_LABELS[role as AssignableRole] ?? role}
                        </Badge>
                      ))}
                      {user.roles.length > 2 ? (
                        <span
                          title={user.roles.map((role) => ROLE_LABELS[role as AssignableRole] ?? role).join(', ')}
                          className="shrink-0 rounded-md bg-[#eef4f6] px-1.5 py-0.5 text-xs font-medium text-[#5f7580]"
                        >
                          +{user.roles.length - 2}
                        </span>
                      ) : null}
                    </div>
                    <RoleMultiSelect
                      roles={user.roles}
                      disabled={isUpdating || (!canManageAdminRoles && hasAdminLevelRole(user.roles))}
                      canManageAdminRoles={canManageAdminRoles}
                      onSave={(nextRoles) => void handleRoleChange(user, nextRoles)}
                    />
                    <div className="flex items-center gap-2">
                      <Select
                        value={aiCohort.assignedCohort ?? aiCohort.effectiveCohort}
                        onValueChange={(value) => void handleAIMealPlanCohortChange(user, value as AIMealPlanCohort)}
                        disabled={aiCohortDisabled}
                      >
                        <SelectTrigger className="h-9 min-w-0 flex-1 border-[#cde3e9] bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_MEAL_PLAN_COHORT_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="shrink-0 rounded-md bg-[#f0f5f7] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] text-[#6b7c85]">
                        {getAIMealPlanCohortSourceLabel(aiCohort)}
                      </span>
                    </div>
                    <div className="whitespace-nowrap text-sm text-[#5d6f78]">
                      {isUpdating ? (
                        <span className="inline-flex items-center gap-2 text-[#168fb4]">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#94a8b0]" />
                          {formatDate(user.lastLoginAt)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            </div>
           </div>
          </div>

          {users.length > 0 ? (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[#5d6f78]">
                Page {safePage} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1 || isLoadingUsers}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages || isLoadingUsers}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default UserProvisioningPage;
